// SEO utility functions for dynamic meta tag management
export const updatePageSEO = (seoData: {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
  structuredData?: any;
}) => {
  // Update title
  if (seoData.title) {
    document.title = seoData.title;
  }

  // Update meta tags
  const updateMetaTag = (name: string, content: string, property = false) => {
    const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
    let meta = document.querySelector(selector) as HTMLMetaElement;
    if (!meta) {
      meta = document.createElement('meta');
      if (property) {
        meta.setAttribute('property', name);
      } else {
        meta.setAttribute('name', name);
      }
      document.head.appendChild(meta);
    }
    meta.content = content;
  };

  if (seoData.description) {
    updateMetaTag('description', seoData.description);
  }
  if (seoData.keywords) {
    updateMetaTag('keywords', seoData.keywords);
  }
  if (seoData.ogTitle) {
    updateMetaTag('og:title', seoData.ogTitle, true);
  }
  if (seoData.ogDescription) {
    updateMetaTag('og:description', seoData.ogDescription, true);
  }
  if (seoData.ogUrl) {
    updateMetaTag('og:url', seoData.ogUrl, true);
  }

  // Update canonical link
  if (seoData.canonical) {
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = seoData.canonical;
  }

  // Update structured data
  if (seoData.structuredData) {
    // Remove existing structured data
    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
      existingScript.remove();
    }

    // Add new structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(seoData.structuredData);
    document.head.appendChild(script);
  }

  // Update last-modified
  const lastModified = new Date().toISOString().split('T')[0];
  updateMetaTag('last-modified', lastModified);
};

// Generate structured data for different page types
export const generateStructuredData = {
  homepage: () => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    return {
      "@context": "https://schema.org",
      "@type": "LegalService",
      "name": "おかやま司法書士事務所",
      "description": "岡山市・倉敷市を中心とした岡山県内で司法書士業務を提供",
      "url": `${siteUrl}/`,
      "telephone": "086-012-3456",
      "faxNumber": "086-012-3457",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "○○町1-2-3",
        "addressLocality": "岡山市北区",
        "addressRegion": "岡山県",
        "postalCode": "700-0000",
        "addressCountry": "JP"
      },
      "openingHours": "Mo-Sa 09:00-18:00",
      "founder": {
        "@type": "Person",
        "name": "岡山太郎"
      },
      "foundingDate": "2000",
      "areaServed": {
        "@type": "Place",
        "name": "岡山県"
      },
      "serviceType": ["不動産登記", "商業登記", "相続手続き", "債務整理", "成年後見"],
      "priceRange": "$$",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "司法書士サービス",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "不動産登記",
              "description": "不動産の売買、相続、贈与に関する登記手続き"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "商業登記",
              "description": "会社設立、役員変更、本店移転などの商業登記"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "相続手続き",
              "description": "相続登記、遺言書作成、遺産分割協議書作成"
            }
          }
        ]
      }
    };
  },

  about: () => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    return {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "mainEntity": {
        "@type": "LegalService",
        "name": "おかやま司法書士事務所",
        "description": "2000年設立の岡山県内の司法書士事務所",
        "url": `${siteUrl}/about`,
        "telephone": "086-012-3456",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "○○町1-2-3",
          "addressLocality": "岡山市北区",
          "addressRegion": "岡山県",
          "postalCode": "700-0000",
          "addressCountry": "JP"
        },
        "founder": {
          "@type": "Person",
          "name": "岡山太郎",
          "jobTitle": "代表司法書士"
        },
        "foundingDate": "2000",
        "numberOfEmployees": 5
      }
    };
  },

  blog: () => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    return {
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "おかやま司法書士事務所ブログ",
      "description": "法的手続きに関する有用な情報と事務所の最新情報",
      "url": `${siteUrl}/blog`,
      "publisher": {
        "@type": "Organization",
        "name": "おかやま司法書士事務所",
        "url": `${siteUrl}/`
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `${siteUrl}/blog`
      }
    };
  },

  recruit: () => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    return {
      "@context": "https://schema.org",
      "@type": "JobPosting",
      "title": "司法書士・司法書士補助者募集",
      "description": "岡山県内で司法書士業務に従事していただける方を募集しています",
      "datePosted": "2024-03-01",
      "validThrough": "2024-12-31",
      "employmentType": "FULL_TIME",
      "hiringOrganization": {
        "@type": "Organization",
        "name": "おかやま司法書士事務所",
        "sameAs": `${siteUrl}/`,
        "logo": `${siteUrl}/logo.png`
      },
      "jobLocation": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "○○町1-2-3",
          "addressLocality": "岡山市北区",
          "addressRegion": "岡山県",
          "postalCode": "700-0000",
          "addressCountry": "JP"
        }
      },
      "baseSalary": {
        "@type": "MonetaryAmount",
        "currency": "JPY",
        "value": {
          "@type": "QuantitativeValue",
          "minValue": 220000,
          "maxValue": 500000,
          "unitText": "MONTH"
        }
      },
      "workHours": "9:00-18:00",
      "benefits": "各種社会保険完備、退職金制度、資格取得支援制度"
    };
  },

  faq: (faqs: Array<{question: string, answer: string}>) => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  })
};
